// packages/web/src/components/layout/layouts/CustomerLayout.tsx
// FAANG-GRADE: Dedicated layout for authenticated customers
// Shopping-focused experience with cart, address management, and order tracking

'use client';

import { Avatar, Box, IconButton, Menu, MenuItem, useTheme } from '@mui/material';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { logout } from '../../../lib/auth/logout';
import { AddressProvider } from '../../../contexts/AddressContext';
import CartWidget from '../../cart/CartWidget';
import BottomNavigation from '../BottomNavigation';
import DeploymentIndicator from '../DeploymentIndicator';
import HeaderAddressSelector from '../HeaderAddressSelector';
import HeaderBranding from '../HeaderBranding';
import { BaseHeader } from '../headers/BaseHeader';
import MarketplaceHeaderElements from '../MarketplaceHeaderElements';
import { useAuthContext } from '../providers/AuthProvider';

interface CustomerLayoutProps {
  children: React.ReactNode;
  catalogBranding?: {
    name?: string;
    logo?: string;
    primaryColor?: string;
    website?: string;
    headerText?: string;
  };
}

export const CustomerLayout: React.FC<CustomerLayoutProps> = ({ children, catalogBranding }) => {
  const theme = useTheme();
  const router = useRouter();
  const { user, role } = useAuthContext();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [headerText, setHeaderText] = useState('');
  // mounted guard: prevents SSR/hydration mismatch from AddressProvider
  // SSR + first client render both skip AddressProvider; it activates post-hydration
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Get catalog slug for branding fetch
  const isCatalogPage = router.pathname.startsWith('/c/');
  const catalogSlug = isCatalogPage ? (router.query.catalogInstance as string) : null;
  const homeHref = catalogSlug ? `/c/${catalogSlug}` : '/';

  // Update headerText when catalogBranding prop changes
  useEffect(() => {
    if (catalogBranding?.headerText && catalogBranding.headerText.trim()) {
      // Use headerText from passed branding if available and not empty
      setHeaderText(catalogBranding.headerText.trim());
    } else if (!catalogSlug) {
      // Reset to empty for main marketplace (authenticated users)
      setHeaderText('');
    }
  }, [catalogSlug, catalogBranding]);

  const handleProfileMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleProfileMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleLogout = useCallback(async () => {
    handleProfileMenuClose();

    // FAANG-GRADE: Use unified logout function - single source of truth
    await logout();
  }, [handleProfileMenuClose]);

  const handleProfileClick = useCallback(() => {
    handleProfileMenuClose();
    // Check if we're in a catalog context
    const isCatalogPage = router.pathname.startsWith('/c/');
    if (isCatalogPage && router.query.catalogInstance) {
      router.push(`/c/${router.query.catalogInstance}/settings`);
    } else {
      router.push('/settings');
    }
  }, [handleProfileMenuClose, router]);

  const branding = useMemo(
    () => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          transition: 'all 0.2s ease',
        }}
      >
        <HeaderBranding
          size="xlarge"
          clickable={true}
          href={homeHref}
          branding={catalogBranding}
          showName={false}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            {headerText}
          </Box>
        </Box>
      </Box>
    ),
    [headerText, catalogBranding, homeHref],
  );

  const centerContent = useMemo(
    () => (
      <>
        <MarketplaceHeaderElements />
        <Box sx={{ mr: 2, display: { xs: 'none', md: 'block' } }}>
          <HeaderAddressSelector />
        </Box>
      </>
    ),
    [],
  );

  // Memoize avatar letter to prevent unnecessary re-renders
  const avatarLetter = useMemo(
    () =>
      (user as any)?.firstName?.charAt(0) ||
      (user as any)?.first_name?.charAt(0) ||
      user?.email?.charAt(0) ||
      '',
    [user],
  );

  const actions = useMemo(
    () => (
      <>
        <CartWidget />
        <IconButton
          id="user-menu-button"
          edge="end"
          aria-label="User menu"
          aria-haspopup="menu"
          aria-controls={anchorEl ? 'user-menu' : undefined}
          aria-expanded={anchorEl ? 'true' : undefined}
          data-testid="user-menu-button"
          onClick={handleProfileMenuOpen}
          color="inherit"
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
            {avatarLetter}
          </Avatar>
        </IconButton>
      </>
    ),
    [handleProfileMenuOpen, theme.palette.primary.main, avatarLetter],
  );

  const layout = (
    <Box sx={{ display: 'flex' }}>
      <BaseHeader branding={branding} actions={actions} catalogBranding={catalogBranding}>
        {centerContent}
      </BaseHeader>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: 3,
          py: 0,
          width: '100%',
          // Add bottom padding for bottom navigation
          pb: 7,
        }}
      >
        {/* Toolbar spacer for fixed header */}
        <Box sx={{ minHeight: { xs: '56px', sm: '64px' } }} />

        {children}
      </Box>

      <BottomNavigation
        role={role}
        activePath={router.asPath}
        onNavigate={(path) => router.push(path)}
      />

      {/* <AskTechaddsWidget /> - Disabled for partner profile pages */}
      <DeploymentIndicator />

      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        keepMounted
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        MenuListProps={{ 'aria-labelledby': 'user-menu-button' }}
      >
        <MenuItem onClick={handleProfileClick}>Profile</MenuItem>
        <MenuItem
          onClick={() => {
            handleProfileMenuClose();
            router.push('/about');
          }}
        >
          About Aithon
        </MenuItem>
        <MenuItem onClick={handleLogout} data-testid="user-menu-logout">
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );

  // Wrap in AddressProvider only after hydration to prevent SSR/CSR mismatch.
  // Before mount: HeaderAddressSelector returns null (graceful degradation).
  // After mount: AddressProvider activates and addresses load normally.
  return mounted ? <AddressProvider>{layout}</AddressProvider> : layout;
};
